/**
 * Redux slice for managing claimed player clients within an event.
 * Handles listing clients and kicking (forcibly disconnecting) one.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Client, ClientsState } from '../types/clients';
import * as clientsService from '../services/clientsService';

// =============================================================================
// Initial State
// =============================================================================

const initialState: ClientsState = {
    clients: [],
    isLoading: false,
    error: null,
    kickingIds: [],
    isKickingAll: false,
};

// =============================================================================
// Helper for error extraction
// =============================================================================

function extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: unknown } } };
        const detail = axiosError.response?.data?.detail;

        // Handle string detail (simple error message)
        if (typeof detail === 'string') {
            return detail;
        }

        // Handle array of validation errors (Pydantic format)
        if (Array.isArray(detail)) {
            const messages = detail.map((err: { msg?: string; loc?: string[] }) => {
                const field = err.loc?.slice(-1)[0] || 'field';
                const msg = err.msg || 'validation error';
                return `${field}: ${msg}`;
            });
            return messages.join('; ') || 'Validation error';
        }

        // Handle object with message property
        if (detail && typeof detail === 'object' && 'message' in detail) {
            return String((detail as { message: unknown }).message);
        }

        return 'An error occurred';
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'An error occurred';
}

// =============================================================================
// Async Thunks
// =============================================================================

export const fetchClients = createAsyncThunk<
    Client[],
    { eventId: string },
    { rejectValue: string }
>('clients/fetchClients', async ({ eventId }, { rejectWithValue }) => {
    try {
        return await clientsService.fetchClients(eventId);
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const kickClient = createAsyncThunk<
    string,
    { eventId: string; unitId: string },
    { rejectValue: string }
>('clients/kickClient', async ({ eventId, unitId }, { rejectWithValue }) => {
    try {
        await clientsService.kickClient(eventId, unitId);
        return unitId;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

export const kickAllClients = createAsyncThunk<
    number,
    { eventId: string },
    { rejectValue: string }
>('clients/kickAllClients', async ({ eventId }, { rejectWithValue }) => {
    try {
        const { kicked } = await clientsService.kickAllClients(eventId);
        return kicked;
    } catch (error) {
        return rejectWithValue(extractErrorMessage(error));
    }
});

// =============================================================================
// Slice
// =============================================================================

export const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        clearClientsError: (state) => {
            state.error = null;
        },
        resetClients: () => initialState,
    },
    extraReducers: (builder) => {
        // Fetch clients
        builder
            .addCase(fetchClients.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchClients.fulfilled, (state, action) => {
                state.isLoading = false;
                state.clients = action.payload;
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Failed to fetch clients';
            });

        // Kick client
        builder
            .addCase(kickClient.pending, (state, action) => {
                state.error = null;
                const { unitId } = action.meta.arg;
                if (!state.kickingIds.includes(unitId)) {
                    state.kickingIds.push(unitId);
                }
            })
            .addCase(kickClient.fulfilled, (state, action) => {
                const unitId = action.payload;
                state.kickingIds = state.kickingIds.filter((id) => id !== unitId);
                // Optimistically mark the kicked client inactive so the row
                // reflects the change immediately, even before a refetch.
                const client = state.clients.find((c) => c.unit_id === unitId);
                if (client) {
                    client.is_active = false;
                    if (!client.revoked_at) {
                        client.revoked_at = new Date().toISOString();
                    }
                }
            })
            .addCase(kickClient.rejected, (state, action) => {
                const { unitId } = action.meta.arg;
                state.kickingIds = state.kickingIds.filter((id) => id !== unitId);
                state.error = action.payload || 'Failed to kick client';
            });

        // Kick all clients
        builder
            .addCase(kickAllClients.pending, (state) => {
                state.error = null;
                state.isKickingAll = true;
            })
            .addCase(kickAllClients.fulfilled, (state) => {
                state.isKickingAll = false;
                // Optimistically mark every client inactive.
                const now = new Date().toISOString();
                state.clients.forEach((client) => {
                    client.is_active = false;
                    if (!client.revoked_at) {
                        client.revoked_at = now;
                    }
                });
            })
            .addCase(kickAllClients.rejected, (state, action) => {
                state.isKickingAll = false;
                state.error = action.payload || 'Failed to kick all clients';
            });
    },
});

// Export actions
export const { clearClientsError, resetClients } = clientsSlice.actions;

// Export reducer
export default clientsSlice.reducer;
